// onboarding-retention.js - Tier 1 Educational
export const content = {
  heroStyle: 'dark',
  tag: 'First 90 Days',

  html: `
<p>Here's something most agents don't realize: retention doesn't start when a client is at risk of leaving. It starts the moment they enroll. The first 90 days of a client relationship determine whether they become a loyal, long-term client or a first-year churn statistic.</p>

<p>Research shows 15.6% of new Medicare Advantage enrollees change insurance within their first year. That's roughly 1 in 6 new clients gone before their first anniversary. But agents with strong onboarding processes see far better numbers. The difference is entirely in how you handle those critical early months.</p>

<h2>Why Onboarding Matters So Much</h2>

<h3>The Vulnerability Window</h3>

<p>New clients are in a unique position:</p>
<ul>
<li>They're still figuring out their coverage</li>
<li>They haven't built loyalty yet</li>
<li>They're comparing expectations to reality</li>
<li>Small problems feel bigger because they're new</li>
<li>Competitors can still seem like reasonable alternatives</li>
</ul>

<p>Until a client has used their coverage successfully, resolved an issue with your help, and built a relationship with you, they're uncommitted. Onboarding is about moving them from uncommitted to loyal.</p>

<h3>First Impressions Compound</h3>

<p>A client who has a smooth first 90 days:</p>
<ul>
<li>Trusts you when issues arise later</li>
<li>Gives you the benefit of the doubt</li>
<li>Refers friends and family</li>
<li>Ignores competitor marketing</li>
<li>Stays for years</li>
</ul>

<p>A client who has a rough first 90 days:</p>
<ul>
<li>Questions your recommendations</li>
<li>Assumes problems are your fault</li>
<li>Doesn't refer anyone</li>
<li>Pays attention to other options</li>
<li>Leaves at first opportunity</li>
</ul>

<p>Same client, same plan—different outcomes based on onboarding experience.</p>

<h2>The Client Onboarding Framework</h2>

<h3>Week 1: Confirmation and Welcome</h3>

<p><strong>Goals:</strong> Confirm enrollment processed, provide essential information, set expectations</p>

<p><strong>Actions:</strong></p>
<ul>
<li>Verify enrollment confirmation received</li>
<li>Send welcome message with your contact info</li>
<li>Provide ID card timeline expectations</li>
<li>Explain what happens next (card arrives, coverage starts)</li>
<li>Give them permission to call with any questions</li>
</ul>

<p><strong>Sample message:</strong> "Welcome! Your enrollment is confirmed. You should receive your ID card within [timeframe]. Your coverage starts [date]. I'm here if you have any questions—don't hesitate to reach out."</p>

<h3>Week 2: Check-In</h3>

<p><strong>Goals:</strong> Ensure no early confusion, demonstrate proactive attention</p>

<p><strong>Actions:</strong></p>
<ul>
<li>Quick check-in call or text</li>
<li>Ask if ID card arrived</li>
<li>Ask if they have questions about coverage</li>
<li>Remind them you're available</li>
</ul>

<p><strong>Sample message:</strong> "Hi [Name], just checking in. Did your ID card arrive? Any questions about your new coverage? I'm here to help."</p>

<h3>Week 3-4: First Use Support</h3>

<p><strong>Goals:</strong> Help with first coverage interactions, catch early problems</p>

<p><strong>Actions:</strong></p>
<ul>
<li>Ask if they've used their coverage yet</li>
<li>Inquire about any pharmacy visits, doctor appointments</li>
<li>Proactively address common first-month questions</li>
<li>If issues arose, help resolve immediately</li>
</ul>

<p><strong>Sample message:</strong> "Have you had a chance to use your new coverage yet? Sometimes the first visit or pharmacy trip brings up questions. Let me know if anything came up."</p>

<h3>Month 2: Deeper Check-In</h3>

<p><strong>Goals:</strong> More substantive conversation, relationship building</p>

<p><strong>Actions:</strong></p>
<ul>
<li>Phone call preferred (not just text)</li>
<li>Ask about satisfaction so far</li>
<li>Discuss any coverage questions that have come up</li>
<li>Explain benefits they might not be using</li>
<li>Update your records with any new information</li>
</ul>

<h3>Month 3: Quarterly Review Foundation</h3>

<p><strong>Goals:</strong> Transition from "new client" to "regular client" cadence</p>

<p><strong>Actions:</strong></p>
<ul>
<li>First quarterly review</li>
<li>Comprehensive satisfaction check</li>
<li>Address any lingering issues</li>
<li>Set expectations for ongoing relationship</li>
<li>"I'll check in quarterly, plus call me anytime"</li>
</ul>

<h2>Common Onboarding Failures</h2>

<h3>The Disappearing Agent</h3>

<p>Enrollment closes, agent moves on to next sale, new client never hears from them again until next AEP (if they're still around).</p>

<p><strong>Fix:</strong> Schedule onboarding touchpoints immediately after enrollment. Treat it as non-negotiable.</p>

<h3>The Information Dump</h3>

<p>Agent overwhelms new client with everything about their coverage all at once. Client retains nothing, feels confused.</p>

<p><strong>Fix:</strong> Space information out. Initial basics only. Add detail over time as relevant.</p>

<h3>The Reactive Approach</h3>

<p>Agent waits for client to call with problems instead of proactively checking in.</p>

<p><strong>Fix:</strong> You reach out first. Problems you catch proactively are easier to fix than problems that fester.</p>

<h3>The Generic Treatment</h3>

<p>Same onboarding for everyone regardless of situation, needs, or concerns.</p>

<p><strong>Fix:</strong> Personalize based on what you learned during enrollment. Address their specific concerns.</p>

<h2>Special Onboarding Considerations</h2>

<h3>For MA Clients</h3>
<ul>
<li>Verify their doctors are in-network (confirm, don't assume)</li>
<li>Explain how to check pharmacy formulary</li>
<li>Walk through prior authorization if relevant</li>
<li>Discuss extra benefits and how to use them</li>
</ul>

<h3>For Medigap Clients</h3>
<ul>
<li>Explain how Medicare + supplement work together</li>
<li>Set rate increase expectations early</li>
<li>Help understand Part D coordination</li>
<li>Clarify what's covered vs. not covered</li>
</ul>

<h3>For Dual Eligibles</h3>
<ul>
<li>Explain Medicare/Medicaid coordination</li>
<li>Walk through extra benefits (OTC, transportation)</li>
<li>Provide care coordinator contact</li>
<li>More frequent early touchpoints (weekly not bi-weekly)</li>
</ul>

<h2>Measuring Onboarding Success</h2>

<p>Track these metrics:</p>
<ul>
<li><strong>First-year retention rate:</strong> Are new clients staying through year one?</li>
<li><strong>OEP switching:</strong> Are January-enrolled clients using OEP to leave?</li>
<li><strong>Issue frequency:</strong> How often do new clients report problems?</li>
<li><strong>Issue resolution:</strong> How quickly are problems fixed?</li>
<li><strong>Touchpoint completion:</strong> Are onboarding steps actually happening?</li>
</ul>

<p>If first-year retention is below 85%, your onboarding needs work.</p>

<h2>The Long Game</h2>

<p>Onboarding isn't just about the first 90 days—it's about the next 10 years. Every client you successfully onboard becomes part of your stable, recurring revenue base. Every client you lose in year one was acquisition cost with no return.</p>

<p>The agents with the most stable, valuable books didn't get there by accident. They got there by making sure every new client became a loyal client from day one.</p>

<p>Start strong. Retention follows.</p>
  `,

  faqs: [
    {
      question: "Why is the first 90 days so important for retention?",
      answer: "Research shows 15.6% of new MA enrollees leave within year one. New clients haven't built loyalty yet—they're comparing expectations to reality and small problems feel bigger. Strong onboarding moves them from uncommitted to loyal before they consider leaving."
    },
    {
      question: "What's the minimum onboarding I should do for every new client?",
      answer: "At minimum: Week 1 welcome contact, Week 2 check-in, Month 2 phone call, Month 3 quarterly review. This ensures new clients feel supported through their most vulnerable period. More touchpoints are better, but these are the essentials."
    },
    {
      question: "When should I check if a new client's doctors are in-network?",
      answer: "During enrollment AND in week 1 of onboarding. Verify, don't assume. Network directories can be outdated, or the client might have mentioned doctors you didn't check. Catching network issues early prevents pharmacy or appointment surprises."
    },
    {
      question: "What's the biggest onboarding mistake agents make?",
      answer: "Disappearing after enrollment. Agent closes the sale, moves to the next prospect, and the new client never hears from them again. By the time AEP comes, there's no relationship to defend. Schedule onboarding touchpoints immediately—treat them as non-negotiable."
    },
    {
      question: "Should onboarding be different for different types of clients?",
      answer: "Yes. MA clients need network and prior auth education. Medigap clients need rate increase expectations set. Dual eligibles need more frequent touchpoints and help understanding Medicare/Medicaid coordination. Customize based on their situation and concerns."
    }
  ],

  sources: [
    {
      title: "Turnover Among New Medicare Advantage Enrollees",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9586458/",
      publisher: "AJMC/PMC",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Onboarding is too important to leave to memory. Pro IRP automatically schedules your onboarding touchpoints, reminds you when each step is due, and tracks completion so no new client falls through the cracks.`
};

export default content;
